import { useState } from "react";
import { Plus, X } from "lucide-react";
import { Button, Input } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import type { CommunicationParticipantDto } from "@/api/communications";
import {
  useAddParticipant,
  useRemoveParticipant,
} from "../hooks/useCommunications";
import { ParticipantChip } from "./ParticipantChip";

interface Props {
  entryId: string;
  participants: CommunicationParticipantDto[];
  canEdit: boolean;
}

export function ParticipantList({ entryId, participants, canEdit }: Props) {
  const [showAdd, setShowAdd] = useState(false);
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [role, setRole] = useState("");
  const add = useAddParticipant(entryId);
  const remove = useRemoveParticipant(entryId);
  const { toast } = useToast();

  const submitAdd = async () => {
    if (!name.trim()) return;
    try {
      await add.mutateAsync({
        displayName: name.trim(),
        phoneOrEmail: contact.trim() || null,
        roleHint: role.trim() || null,
        contactId: null,
      });
      setName("");
      setContact("");
      setRole("");
      setShowAdd(false);
    } catch (e) {
      const msg =
        e instanceof ApiError ? e.errors?.[0] ?? e.message : "Could not add.";
      toast({ message: msg, tone: "error" });
    }
  };

  const onRemove = async (participantId: string) => {
    try {
      await remove.mutateAsync(participantId);
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? e.errors?.[0] ?? e.message
          : "Could not remove participant.";
      toast({ message: msg, tone: "error" });
    }
  };

  return (
    <section>
      <div className="flex items-center justify-between mb-space-2">
        <h2 className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow m-0">
          Participants
        </h2>
        {canEdit && !showAdd && (
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-space-1 text-label text-gold-shadow hover:text-gold font-display font-semibold uppercase tracking-label"
          >
            <Plus size={14} aria-hidden /> Add participant
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-space-2">
        {participants.map((p) => (
          <span key={p.id} className="inline-flex items-center gap-space-1">
            <ParticipantChip participant={p} />
            {canEdit && (
              <button
                type="button"
                onClick={() => onRemove(p.id)}
                aria-label={`Remove ${p.displayName}`}
                className="text-gray-mid hover:text-status-error"
              >
                <X size={12} aria-hidden />
              </button>
            )}
          </span>
        ))}
        {participants.length === 0 && (
          <p className="text-small text-gray-mid m-0">No participants recorded.</p>
        )}
      </div>
      {canEdit && showAdd && (
        <div className="mt-space-3 grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr_auto] gap-space-2">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Display name"
            aria-label="Participant name"
          />
          <Input
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            placeholder="Phone / email"
            aria-label="Participant phone or email"
          />
          <Input
            value={role}
            onChange={(e) => setRole(e.target.value)}
            placeholder="Role hint"
            aria-label="Participant role"
          />
          <div className="flex gap-space-1">
            <Button size="sm" type="button" onClick={submitAdd} loading={add.isPending}>
              Add
            </Button>
            <Button
              size="sm"
              variant="secondary"
              type="button"
              onClick={() => setShowAdd(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
